import * as jsoncParser from 'jsonc-parser';
import * as monacoEditor from 'monaco-editor';

import { JSONValue } from 'src/app/graph/explorerPage/helpers/types';

import { CurrentDocsContext, Selection } from './getCurrentDocsContext';

export interface Deletion {
  type: 'deleteDocNode';
  edits: monacoEditor.editor.IIdentifiedSingleEditOperation[];
  variableEdits: jsoncParser.Edit[][];
}
/**
 * value can be removed if all leaf nodes are `[]`, `{}`, or `null`
 * and no array is longer then 1
 */
function canRemoveVariableValue(value: unknown): boolean {
  if (typeof value !== 'object' || (Array.isArray(value) && value.length > 1)) {
    return false;
  }
  return Object.values(value || {}).every(canRemoveVariableValue);
}

function getVariableEdits(
  variableModel: monacoEditor.editor.ITextModel,
  removedVariables: string[],
  variableOperationCount: CurrentDocsContext['variableOperationCount'],
): jsoncParser.Edit[][] {
  let currentValue = variableModel.getValue();
  const variables: JSONValue = jsoncParser.parse(currentValue) ?? {};
  return removedVariables
    .filter(
      (variableName) =>
        typeof variables === 'object' &&
        variables &&
        !Array.isArray(variables) &&
        canRemoveVariableValue(variables[variableName]) &&
        variableOperationCount[variableName] === 1,
    )
    .map((variableName) => {
      const edits = jsoncParser.modify(
        currentValue,
        [variableName],
        undefined,
        { formattingOptions: { insertSpaces: true, tabSize: 2 } },
      );
      currentValue = jsoncParser.applyEdits(currentValue, edits);
      return edits;
    });
}

function getRemovableVariables(
  currentDocsContext: CurrentDocsContext,
  fieldsToDelete: Selection[],
) {
  const totalVariableUsage: { [variable: string]: number } = {};
  currentDocsContext.variables.forEach((v) => {
    if (v.usageCount > 0) totalVariableUsage[v.name] = v.usageCount;
  });
  fieldsToDelete.forEach((field) =>
    Object.entries(field.variableUsage || {}).forEach(([key, value]) => {
      totalVariableUsage[key] -= value;
    }),
  );

  return Object.entries(totalVariableUsage)
    .filter(([, value]) => value < 1)
    .map(([key]) => key);
}

function getVariableDefinitionEdits(
  currentDocsContext: CurrentDocsContext,
  removedVariables: string[],
) {
  return removedVariables.length === currentDocsContext.variables.length &&
    currentDocsContext.variableDefinitionRange
    ? [{ range: currentDocsContext.variableDefinitionRange, text: null }]
    : currentDocsContext.variables.reduce(
        (
          edits: Array<{ range: monacoEditor.Range; text: null }>,
          variable,
          i,
        ) => {
          if (!removedVariables.includes(variable.name)) {
            // not removed, no edit required for this variable
            return edits;
          }

          // nextVariable is only used when the edit is removing from the start of the definition.
          // In this function there is at least 1 variable not being removed so this is safe.
          const nextVariable = currentDocsContext.variables[i + 1];

          if (i === 0) {
            // It's the first variable in the definition so remove up to the
            // start of the next variable
            return [
              ...edits,
              {
                range: monacoEditor.Range.fromPositions(
                  variable.range.getStartPosition(),
                  nextVariable?.range.getStartPosition(),
                ),
                text: null,
              },
            ];
          }
          const previousVariable = currentDocsContext.variables[i - 1];
          if (
            previousVariable &&
            removedVariables.includes(previousVariable.name)
          ) {
            // previous variable was removed, add to the existing edits range
            const lastEdit = edits[edits.length - 1];
            const firstCurrentDocsContextVariable =
              currentDocsContext.variables[0];
            if (
              firstCurrentDocsContextVariable &&
              nextVariable &&
              lastEdit?.range
                .getStartPosition()
                .equals(
                  firstCurrentDocsContextVariable.range.getStartPosition(),
                )
            ) {
              // previous edit starts from the first variable, remove to the start of the next variable
              const { lineNumber, column } =
                nextVariable.range.getStartPosition();
              lastEdit.range = lastEdit.range.setEndPosition(
                lineNumber,
                column,
              );
            } else if (lastEdit) {
              // current edit starts somewhere after the first variable, remove to end of this variable.
              lastEdit.range = lastEdit.range.plusRange(variable.range);
            }

            return edits;
          }
          // it's not the first variable and the previous variable wasn't removed
          // remove from end of previous variable
          return [
            ...edits,
            {
              range: monacoEditor.Range.fromPositions(
                // TODO: Remove non null assertion, added when turning on noUncheckedIndexedAccess
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                previousVariable!.range.getEndPosition(),
                variable.range.getEndPosition(),
              ),
              text: null,
            },
          ];
        },
        [],
      );
}

/**
 * Remove a selection set matching the names given, and clean up whitespace
 */
export function getDeletion(
  currentDocsContext: CurrentDocsContext,
  nodeNamesToDelete: Set<string>,
  model: monacoEditor.editor.ITextModel,
  variableModel: monacoEditor.editor.ITextModel | null | undefined,
  shouldManageVariables: boolean,
): Deletion {
  // If this was not a full match for the docs, there should be nothing to
  // delete
  if (
    currentDocsContext.matchType !== 'full' &&
    currentDocsContext.matchType !== 'root'
  ) {
    return { type: 'deleteDocNode', edits: [], variableEdits: [] };
  }

  const fieldsToDelete = currentDocsContext.selectionSet.filter(
    (field) => field.name && nodeNamesToDelete.has(field.name),
  );

  const removedVariables = getRemovableVariables(
    currentDocsContext,
    fieldsToDelete,
  );

  return {
    type: 'deleteDocNode',
    variableEdits:
      shouldManageVariables && variableModel
        ? getVariableEdits(
            variableModel,
            removedVariables,
            currentDocsContext.variableOperationCount,
          )
        : [],
    edits: [
      ...fieldsToDelete.map((field) => {
        const startIndex = model.getOffsetAt(field.range.getStartPosition());
        // Trim preceding whitespace, this will always match something
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const whiteSpaceIndex = model
          .getValue()
          .slice(0, startIndex)
          .match(/\s*$/)!.index!;

        return {
          range: monacoEditor.Range.fromPositions(
            model.getPositionAt(whiteSpaceIndex),
            field.range.getEndPosition(),
          ),
          text: null,
        };
      }),
      ...(currentDocsContext.matchType === 'root' || !shouldManageVariables
        ? []
        : getVariableDefinitionEdits(currentDocsContext, removedVariables)),
    ],
  };
}

/**
 * Remove all fields in the current level selection set, and clean up whitespace
 */
export function getAllFieldsDeletion(
  currentDocsContext: CurrentDocsContext,
  model: monacoEditor.editor.ITextModel,
  variableModel: monacoEditor.editor.ITextModel | null | undefined,
  shouldManageVariables: boolean,
): Deletion {
  const allFieldsNames =
    currentDocsContext.matchType === 'full' ||
    currentDocsContext.matchType === 'root'
      ? new Set(
          currentDocsContext.selectionSet.flatMap((field) => field.name ?? []),
        )
      : new Set([]);
  return getDeletion(
    currentDocsContext,
    allFieldsNames,
    model,
    variableModel,
    shouldManageVariables,
  );
}

/**
 * Remove a selection set matching the name given, and clean up whitespace
 */
export function getArgumentDeletion(
  currentDocsContext: CurrentDocsContext,
  argumentNameToDelete: string,
  variableModel: monacoEditor.editor.ITextModel | null | undefined,
  shouldManageVariables: boolean,
): Deletion {
  const argument = currentDocsContext.arguments.find(
    (arg) => arg.name === argumentNameToDelete,
  );
  // If this was not a full match for the docs, there should be nothing to
  // delete
  if (currentDocsContext.matchType === 'none' || !argument) {
    return { type: 'deleteDocNode', edits: [], variableEdits: [] };
  }

  const removedVariable =
    argument.isVariable &&
    currentDocsContext.variables.find(
      (v) => v.usageCount === 1 && v.name === argument.value,
    )?.name;

  const indexOfRemovedVariable = currentDocsContext.arguments.findIndex(
    (arg) => arg.name === argumentNameToDelete,
  );
  const removedVariables = removedVariable ? [removedVariable] : [];
  return {
    type: 'deleteDocNode',
    variableEdits:
      shouldManageVariables && variableModel
        ? getVariableEdits(
            variableModel,
            removedVariables,
            currentDocsContext.variableOperationCount,
          )
        : [],
    edits: [
      {
        range:
          currentDocsContext.arguments.length === 1 &&
          currentDocsContext.argumentsRange
            ? currentDocsContext.argumentsRange
            : indexOfRemovedVariable === 0
              ? monacoEditor.Range.fromPositions(
                  argument.range.getStartPosition(),
                  currentDocsContext.arguments[1]?.range.getStartPosition(),
                )
              : monacoEditor.Range.fromPositions(
                  // TODO: Remove non null assertion, added when turning on noUncheckedIndexedAccess
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  currentDocsContext.arguments[
                    indexOfRemovedVariable - 1
                  ]!.range.getEndPosition(),
                  argument.range.getEndPosition(),
                ),
        text: null,
      },
      ...(shouldManageVariables
        ? getVariableDefinitionEdits(currentDocsContext, removedVariables)
        : []),
    ],
  };
}
